import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getLatestRouteTrip } from '../../APIs';
import { StoreContext } from '../../Library/contexts';
import PATHS from '../../Routes/paths';
import PinInputField from './PinInputField';

const ROUTE_TRIP_CREATED = 1;

// ─── SVGs ─────────────────────────────────────────────────────────────────────

const StickFigureSvg = () => (
  <svg viewBox="0 0 28 52" fill="none" className="dl-stick-figure">
    <circle cx="14" cy="5.5" r="5" fill="#4DA3FF" />
    {/* body */}
    <line x1="14" y1="11" x2="14" y2="29" stroke="#4DA3FF" strokeWidth="2.5" strokeLinecap="round" />
    {/* backpack */}
    <rect x="14" y="14" width="6" height="8" rx="1.5" fill="#2563EB" opacity="0.7" />
    {/* arms */}
    <line className="dl-arm-l" x1="14" y1="16" x2="4"  y2="23" stroke="#4DA3FF" strokeWidth="2" strokeLinecap="round" />
    <line className="dl-arm-r" x1="14" y1="16" x2="24" y2="23" stroke="#4DA3FF" strokeWidth="2" strokeLinecap="round" />
    {/* legs */}
    <line className="dl-leg-l" x1="14" y1="29" x2="6"  y2="46" stroke="#4DA3FF" strokeWidth="2.5" strokeLinecap="round" />
    <line className="dl-leg-r" x1="14" y1="29" x2="22" y2="46" stroke="#4DA3FF" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const BusSvg = () => (
  <svg viewBox="0 0 80 40" fill="none" className="dl-bus-svg">
    {/* body */}
    <rect x="2" y="3" width="76" height="26" rx="5" fill="#1D4ED8" />
    {/* roof highlight */}
    <rect x="4" y="3" width="72" height="7" rx="4" fill="rgba(255,255,255,0.08)" />
    {/* stripe */}
    <rect x="2" y="17" width="76" height="4" fill="rgba(255,255,255,0.06)" />
    {/* windows */}
    <rect x="6"  y="6" width="13" height="9" rx="2" fill="rgba(147,210,255,0.55)" />
    <rect x="23" y="6" width="13" height="9" rx="2" fill="rgba(147,210,255,0.55)" />
    <rect x="40" y="6" width="13" height="9" rx="2" fill="rgba(147,210,255,0.55)" />
    {/* door */}
    <rect x="57" y="6" width="10" height="9" rx="1.5" fill="rgba(147,210,255,0.3)" />
    <line x1="62" y1="6" x2="62" y2="15" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
    {/* front */}
    <rect x="70" y="8" width="7" height="6" rx="1.5" fill="rgba(200,230,255,0.4)" />
    {/* headlight glow */}
    <circle cx="77" cy="11" r="3" fill="rgba(220,240,255,0.25)" />
    {/* bumper */}
    <rect x="2" y="27" width="76" height="3" rx="1" fill="rgba(255,255,255,0.12)" />
    {/* wheels */}
    <circle className="dl-wheel" cx="18" cy="35" r="5" fill="#0f172a" />
    <circle cx="18" cy="35" r="2.5" fill="#334155" />
    <circle className="dl-wheel" cx="62" cy="35" r="5" fill="#0f172a" />
    <circle cx="62" cy="35" r="2.5" fill="#334155" />
    {/* wheel spokes */}
    <line className="dl-spoke" x1="18" y1="30" x2="18" y2="33" stroke="#475569" strokeWidth="1" strokeLinecap="round" />
    <line className="dl-spoke" x1="21" y1="32" x2="19.5" y2="34" stroke="#475569" strokeWidth="1" strokeLinecap="round" />
    <line className="dl-spoke" x1="62" y1="30" x2="62" y2="33" stroke="#475569" strokeWidth="1" strokeLinecap="round" />
    <line className="dl-spoke" x1="65" y1="32" x2="63.5" y2="34" stroke="#475569" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

const CheckSvg = () => (
  <svg viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill="#007AFF" />
    <path d="M5 9l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Loading Scene (appears only AFTER PIN is verified, while APIs finish) ────

const STEPS = [
  { label: 'Driver verified' },
  { label: 'Loading routes' },
  { label: 'Checking active trips' },
];

const SCENE_TEXTS = [
  'Driver verified! Loading your data…',
  'Fetching available routes…',
  'Almost ready, checking trips…',
];

const ExhaustPuff = ({ delay }) => (
  <div className="dl-exhaust-puff" style={{ animationDelay: `${delay}s` }} />
);

const LoadingScene = ({ step }) => (
  <div className="dl-loading-scene">
    <div className="dl-scene-stage">
      {/* Sky gradient */}
      <div className="dl-sky" />

      {/* Road */}
      <div className="dl-road">
        <div className="dl-road-line" />
        <div className="dl-road-line" />
        <div className="dl-road-line" />
        <div className="dl-road-line" />
        <div className="dl-road-line" />
      </div>

      {/* Driver walks in then boards → steps 0-1 */}
      <AnimatePresence>
        {step <= 1 && (
          <motion.div
            key="driver"
            className="dl-driver-walk"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: step === 0 ? 10 : 52, opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <StickFigureSvg />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bus rolls in → step 1+ */}
      <AnimatePresence>
        {step >= 1 && (
          <motion.div
            key="bus"
            className="dl-bus-container"
            initial={{ x: -130, opacity: 0 }}
            animate={{ x: step === 1 ? -10 : 20, opacity: 1 }}
            exit={{ x: 200, opacity: 0, transition: { duration: 0.6, ease: 'easeIn' } }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          >
            <BusSvg />
            {/* Exhaust puffs from back of bus */}
            <div className="dl-exhaust">
              <ExhaustPuff delay={0} />
              <ExhaustPuff delay={0.4} />
              <ExhaustPuff delay={0.8} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All steps done → bus drives off */}
      {step >= STEPS.length && (
        <motion.div
          key="bus-exit"
          className="dl-bus-container"
          initial={{ x: 20, opacity: 1 }}
          animate={{ x: 300, opacity: 0 }}
          transition={{ duration: 0.7, ease: 'easeIn', delay: 0.1 }}
        >
          <BusSvg />
        </motion.div>
      )}
    </div>

    {/* Status text */}
    <div className="dl-status-text-container">
      <AnimatePresence mode="wait">
        <motion.p
          key={step}
          className="dl-status-text"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.28 }}
        >
          {SCENE_TEXTS[Math.min(step, SCENE_TEXTS.length - 1)]}
        </motion.p>
      </AnimatePresence>
    </div>

    {/* Steps */}
    <div className="dl-steps">
      {STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <motion.div
            key={i}
            className={`dl-step${done ? ' dl-step-done' : active ? ' dl-step-active' : ''}`}
            animate={{ opacity: i > step ? 0.3 : 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="dl-step-icon">
              {done ? (
                <motion.div
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                >
                  <CheckSvg />
                </motion.div>
              ) : (
                <div className={`dl-step-dot${active ? ' dl-step-dot-pulse' : ''}`} />
              )}
            </div>
            <span className="dl-step-label">{s.label}</span>
          </motion.div>
        );
      })}
    </div>
  </div>
);

// ─── Main Overlay ─────────────────────────────────────────────────────────────

const DriverLoginOverlay = ({ prefetchDone, prefetchedRoutes, prefetchedActiveTrip, onDismiss }) => {
  const navigate = useNavigate();
  const { userVehicles } = useContext(StoreContext);

  // Start directly on PIN — loader only appears if APIs aren't done yet after PIN
  const [phase, setPhase] = useState('pin'); // 'pin' | 'loading'
  const [loadingStep, setLoadingStep] = useState(0);

  const verifiedDriverRef = useRef(null);
  const prefetchDoneRef = useRef(prefetchDone);

  // Keep ref in sync so callbacks have fresh value
  useEffect(() => { prefetchDoneRef.current = prefetchDone; }, [prefetchDone]);

  // Advance loading steps while in loading phase
  useEffect(() => {
    if (phase !== 'loading') return;
    setLoadingStep(0);
    let step = 0;
    const interval = setInterval(() => {
      step += 1;
      if (step < STEPS.length) {
        setLoadingStep(step);
      } else {
        clearInterval(interval);
      }
    }, 900);
    return () => clearInterval(interval);
  }, [phase]);

  // When prefetch finishes while we're in loading phase → complete + navigate
  useEffect(() => {
    if (!prefetchDone || phase !== 'loading' || !verifiedDriverRef.current) return;
    setLoadingStep(STEPS.length); // snap all steps to done
    setTimeout(() => handleDriverVerified(verifiedDriverRef.current), 550);
  }, [prefetchDone, phase]);

  const handleDriverVerified = useCallback((driver) => {
    const routes = prefetchedRoutes?.current || [];

    // Query by this driver's idx directly — avoids stale forAccount prefetch.
    // onDismiss is called AFTER the active-trip check so the overlay is not
    // removed while we still have an outstanding async operation.
    getLatestRouteTrip({ driverId: driver.idx }, (err, activeTrip) => {
      const hasOngoingTrip =
        !err &&
        activeTrip &&
        activeTrip.status === ROUTE_TRIP_CREATED &&
        !activeTrip.endTs;

      // Dismiss the overlay now that all async work is done
      onDismiss(driver, Date.now());

      if (!hasOngoingTrip) return;

      const vehicle = (userVehicles || []).find(v =>
        String(v.idx) === String(activeTrip.vid) || String(v.vid) === String(activeTrip.vid)
      );
      const route = routes.find(r => String(r.idx) === String(activeTrip.routeId));

      navigate(
        vehicle && route
          ? `${PATHS.DRIVER_ROUTE_CHECK}/${PATHS.NAVIGATION}`
          : `${PATHS.DRIVER_ROUTE_CHECK}/${PATHS.SELECT_BUS}`,
        {
          state: vehicle && route ? { vehicle, route, tripId: activeTrip.idx, driverId: driver.idx } : undefined,
          replace: true,
        }
      );
    });
  }, [prefetchedRoutes, userVehicles, onDismiss, navigate]);

  const handlePinVerified = useCallback((driver) => {
    if (prefetchDoneRef.current) {
      // APIs already done — proceed immediately
      handleDriverVerified(driver);
    } else {
      // Still loading — show loader while waiting
      verifiedDriverRef.current = driver;
      setPhase('loading');
    }
  }, [handleDriverVerified]);

  return (
    <div className="dl-overlay">
      <div className="dl-overlay-bg">
        <div className="dl-aurora" />
      </div>

      <div className="dl-content">
        <AnimatePresence mode="wait">
          {phase === 'pin' && (
            <motion.div
              key="pin"
              className="dl-pin-card"
              initial={{ opacity: 0, scale: 0.88, y: 28 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            >
              <PinInputField
                onVerified={handlePinVerified}
                title="Identify Yourself"
                subtitle="Enter your 4-digit driver PIN to continue."
              />
            </motion.div>
          )}

          {phase === 'loading' && (
            <motion.div
              key="loading"
              className="dl-card"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="dl-card-title">Setting Up</p>
              <LoadingScene step={loadingStep} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DriverLoginOverlay;
